import { useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * @typedef {Object} PropertyFilters
 * @property {string} priceType - 'sale' | 'rent' | ''
 * @property {string} type      - 'Apartment' | 'Villa' | 'Studio' | 'Commercial' | ''
 * @property {string} emirate   - Emirates name | ''
 * @property {string} beds      - '1'|'2'|'3'|'4' | ''
 * @property {string} maxPrice  - stringified number | ''
 */

/**
 * @typedef {'default' | 'price-asc' | 'price-desc'} SortOrder
 */

/**
 * Encapsulates all filter/sort logic for the Listings page.
 * Reads initial values from URL search params so filters survive a refresh.
 *
 * @param {Array} properties - Full unfiltered property list.
 * @returns {{
 *   filters: PropertyFilters,
 *   sort: SortOrder,
 *   filtered: Array,
 *   handleFilterChange: (key: string, value: string) => void,
 *   handleSortChange: (order: SortOrder) => void,
 *   handleReset: () => void,
 * }}
 */
export function usePropertyFilters(properties) {
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState(
    /** @type {PropertyFilters} */ ({
      priceType: searchParams.get("priceType") ?? "",
      type:      searchParams.get("type")      ?? "",
      emirate:   searchParams.get("emirate")   ?? "",
      beds:      searchParams.get("beds")      ?? "",
      maxPrice:  searchParams.get("maxPrice")  ?? "",
    })
  );

  const [sort, setSort] = useState(/** @type {SortOrder} */ ("default"));

  const handleFilterChange = useCallback(
    (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
    []
  );

  const handleSortChange = useCallback((order) => setSort(order), []);

  const handleReset = useCallback(
    () => setFilters({ priceType: "", type: "", emirate: "", beds: "", maxPrice: "" }),
    []
  );

  const filtered = useMemo(() => {
    let result = [...properties];

    if (filters.priceType) result = result.filter((p) => p.priceType === filters.priceType);
    if (filters.type)      result = result.filter((p) => p.type      === filters.type);
    if (filters.emirate)   result = result.filter((p) => p.emirate   === filters.emirate);

    if (filters.beds) {
      const bedsNum = parseInt(filters.beds, 10);
      // Guard against NaN from malformed URL params (e.g. ?beds=foo)
      if (!isNaN(bedsNum)) {
        result = result.filter((p) =>
          bedsNum >= 4 ? p.beds >= 4 : p.beds === bedsNum
        );
      }
    }

    if (filters.maxPrice) {
      const max = parseInt(filters.maxPrice, 10);
      // Guard against NaN from malformed URL params (e.g. ?maxPrice=foo)
      if (!isNaN(max)) {
        result = result.filter((p) => p.price <= max);
      }
    }

    if (sort === "price-asc")  result.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") result.sort((a, b) => b.price - a.price);

    return result;
  }, [filters, sort, properties]);

  return { filters, sort, filtered, handleFilterChange, handleSortChange, handleReset };
}
